import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Grid3X3, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, X,
  Wallet, Info, Trash2, RefreshCw, Target, PauseCircle, PlayCircle, Plus,
  ChevronDown, ChevronUp, Sparkles, Zap, Activity,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import useTokenPrice from '../hooks/useTokenPrice';
import {
  prepareDexGridBot, commitDexGridBot,
  listDexGridBots, pauseDexGridBot, resumeDexGridBot, cancelDexGridBot,
  refillPrepareDexGridBot, refillCommitDexGridBot,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor, chartPairFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;

const EXPIRY_OPTIONS = [
  { label: '7 days',   sec: 7 * 86400 },
  { label: '14 days',  sec: 14 * 86400 },
  { label: '30 days',  sec: 30 * 86400 },
  { label: '90 days',  sec: 90 * 86400 },
];

const STATUS_STYLE = {
  armed:     { label: 'Armed',     cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: Grid3X3 },
  paused:    { label: 'Paused',    cls: 'bg-amber-500/10 text-amber-300 border-amber-500/20',       icon: PauseCircle },
  completed: { label: 'Completed', cls: 'bg-blue-500/10 text-blue-300 border-blue-500/20',          icon: CheckCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-dark-600 text-gray-400 border-dark-500',                icon: X },
  failed:    { label: 'Failed',    cls: 'bg-red-500/10 text-red-400 border-red-500/20',             icon: AlertTriangle },
};

// Symbol → CoinGecko id for the live-price hook. Covers the buy-side tokens
// users actually grid against. Wrapped tokens map to their underlying since
// Chainlink (and the price ticker) report the underlying.
const SYMBOL_TO_CG = {
  WETH: 'ethereum',  ETH: 'ethereum',
  WBTC: 'bitcoin',   BTC: 'bitcoin',  cbBTC: 'bitcoin',  'BTC.b': 'bitcoin',
  WMATIC: 'matic-network', MATIC: 'matic-network', POL: 'matic-network',
  WBNB: 'binancecoin', BNB: 'binancecoin',
  WAVAX: 'avalanche-2', AVAX: 'avalanche-2',
  LINK: 'chainlink', UNI: 'uniswap', AAVE: 'aave',
  ARB: 'arbitrum', OP: 'optimism', SOL: 'solana',
  CRV: 'curve-dao-token', COMP: 'compound-governance-token',
  MKR: 'maker', SNX: 'havven',
};

// Quick-start templates. Each takes the live current price and sets a range
// around it. `rangePct` is the half-width (so ±5% → low = current × 0.95).
const QUICK_START_TEMPLATES = [
  { id: 'tight',     label: 'Tight',     rangePct: 5,  gridCount: 10, expirySec: 14 * 86400, desc: '±5% range · 10 levels · 14d' },
  { id: 'standard',  label: 'Standard',  rangePct: 10, gridCount: 5,  expirySec: 30 * 86400, desc: '±10% range · 5 levels · 30d' },
  { id: 'wide',      label: 'Wide',      rangePct: 20, gridCount: 7,  expirySec: 30 * 86400, desc: '±20% range · 7 levels · 30d' },
  { id: 'longterm',  label: 'Long-term', rangePct: 30, gridCount: 5,  expirySec: 90 * 86400, desc: '±30% range · 5 levels · 90d' },
];

// Estimated protocol fee per fill — used to net-out the gross profit estimate.
// CoW typically ~0% taker on solver auctions; Velora similar. We surface 0.1%
// as a conservative round-trip cushion so the user isn't misled.
const ESTIMATED_FEE_ROUND_TRIP_PCT = 0.1;

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function fmtUsd(n, frac = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac });
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.armed;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${s.cls}
      ${status === 'armed' ? 'animate-[grid-pulse_2s_ease-in-out_infinite]' : ''}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

// ─── Pool capacity bar ──────────────────────────────────────────────────────
// Visual at-a-glance health of the pre-signed refill pool. Splits into 3
// states: healthy (>30% remaining of original), warning (≤30%), critical (≤10%).
function PoolCapacityBar({ pool }) {
  const total = Array.isArray(pool) ? pool.length : 0;
  const remaining = Array.isArray(pool) ? pool.filter(e => !e.used).length : 0;
  const pct = total > 0 ? (remaining / total) * 100 : 0;
  const tone = pct <= 10 ? 'bg-red-500'
             : pct <= 30 ? 'bg-amber-400'
             : 'bg-emerald-500';
  const textTone = pct <= 10 ? 'text-red-400'
                 : pct <= 30 ? 'text-amber-300'
                 : 'text-emerald-300';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-dark-600 rounded-full overflow-hidden min-w-[60px] max-w-[140px]">
        <div className={`h-full ${tone} transition-all duration-500`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      <span className={`text-[10px] font-mono font-medium ${textTone} whitespace-nowrap`}>
        {remaining}/{total} left
      </span>
    </div>
  );
}

// ─── Live price badge ───────────────────────────────────────────────────────
// Shows the live USD price of `symbol` via the existing useTokenPrice hook.
// Mounts only when we have a known CoinGecko id — falls back to a hint
// otherwise (e.g. for tokens we haven't mapped yet, custom imports, etc.).
function LivePriceBadge({ symbol, onSetRange }) {
  const cgId = SYMBOL_TO_CG[symbol] || null;
  const { data, loading } = useTokenPrice(cgId, 30000);
  const price = data?.currentPriceUsd ?? data?.market_data?.current_price?.usd ?? data?.current_price ?? data?.usd ?? null;
  if (!cgId) {
    return (
      <div className="bg-dark-700/60 border border-dark-600 rounded-xl px-3 py-2 text-[11px] text-gray-500 flex items-center gap-2">
        <Info className="w-3 h-3" /> Live price unavailable for {symbol} — enter low/high manually
      </div>
    );
  }
  return (
    <div className="bg-dark-700/60 border border-dark-600 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Activity className={`w-3 h-3 flex-shrink-0 ${loading ? 'text-gray-500' : 'text-emerald-400 animate-pulse'}`} />
        <span className="text-[11px] text-gray-400 whitespace-nowrap">{symbol} live:</span>
        <span className="text-sm text-white font-semibold whitespace-nowrap">
          {price != null ? `$${fmtUsd(price, price >= 1 ? 2 : 4)}` : (loading ? '…' : '—')}
        </span>
      </div>
      {price != null && (
        <div className="flex gap-1 flex-shrink-0">
          {[5, 10, 20].map(pct => (
            <button key={pct} type="button"
              onClick={() => onSetRange(price, pct)}
              title={`Set range to ±${pct}% around current price`}
              className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-dark-800 hover:bg-accent-primary/15 text-gray-400 hover:text-accent-primary border border-dark-600 hover:border-accent-primary/30 transition-all">
              ±{pct}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Visual grid ladder ─────────────────────────────────────────────────────
// Renders price levels as horizontal lines on a vertical scale. Used in both
// the create-panel preview AND the active-grid expanded detail. Buys are green,
// sells red, the current price marker is white and animated.
//
// Props:
//   levels:        Array<number> (price values, low → high)
//   currentPrice:  Number | null
//   levelMeta?:    Array<{ levelIdx, side: 'buy'|'sell'|'idle', filled?: boolean }>
//                  If omitted, sides are inferred from currentPrice.
//   sellSym, buySym: labels
//   height?:       pixel height (default 220)
function GridLadder({ levels, currentPrice, levelMeta, sellSym, buySym, height = 220 }) {
  if (!Array.isArray(levels) || levels.length < 2) {
    return (
      <div className="bg-dark-700/30 border border-dashed border-dark-600 rounded-xl px-4 py-8 text-center">
        <Grid3X3 className="w-6 h-6 text-gray-600 mx-auto mb-1.5" />
        <p className="text-gray-500 text-xs">Set low/high prices to preview the grid ladder</p>
      </div>
    );
  }
  const low  = levels[0];
  const high = levels[levels.length - 1];
  const range = high - low || 1;
  const cp = Number(currentPrice);
  const cpInRange = Number.isFinite(cp) && cp > 0;
  // Clamp the price marker so it's visible even if price is outside the range.
  const clampedCp = cpInRange ? Math.min(Math.max(cp, low - range * 0.05), high + range * 0.05) : null;

  function yForPrice(p) {
    // Top of SVG = high price. Bottom = low price.
    return ((high - p) / range) * (height - 24) + 12;
  }

  function sideForLevel(idx, price) {
    if (Array.isArray(levelMeta) && levelMeta[idx]) {
      return levelMeta[idx];
    }
    if (cpInRange) {
      return { side: price < cp ? 'buy' : 'sell' };
    }
    // No live price → infer all lower-half as buys, upper-half as sells
    return { side: idx < levels.length / 2 ? 'buy' : 'sell' };
  }

  return (
    <div className="bg-gradient-to-b from-dark-700/40 to-dark-700/10 border border-dark-600 rounded-xl px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Grid ladder</span>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1 text-emerald-400"><span className="w-2 h-px bg-emerald-400 inline-block" /> Buy</span>
          <span className="flex items-center gap-1 text-red-400"><span className="w-2 h-px bg-red-400 inline-block" /> Sell</span>
          {cpInRange && <span className="flex items-center gap-1 text-white"><span className="w-2 h-px bg-white inline-block" /> Current</span>}
        </div>
      </div>
      <div className="relative" style={{ height }}>
        <svg width="100%" height={height} className="overflow-visible">
          {/* Background scale line */}
          <line x1="12%" y1={yForPrice(high)} x2="12%" y2={yForPrice(low)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="2 3" />
          {/* Grid levels */}
          {levels.map((p, idx) => {
            const meta = sideForLevel(idx, p);
            const isIdle = meta.side === 'idle';
            const isBuy = meta.side === 'buy';
            const color = isIdle ? 'rgba(156,163,175,0.55)'
                        : isBuy ? 'rgb(52,211,153)' : 'rgb(248,113,113)';
            const opacity = meta.filled ? 0.4 : 1;
            const y = yForPrice(p);
            return (
              <g key={idx} opacity={opacity}>
                <line x1="12%" y1={y} x2="78%" y2={y} stroke={color} strokeWidth="1.5"
                  strokeDasharray={isIdle ? '3 3' : undefined} />
                <circle cx="12%" cy={y} r="3" fill={color} opacity={isIdle ? 0.6 : 1} />
                <text x="80%" y={y + 3.5} fill={color} fontSize="10" fontFamily="ui-monospace,monospace" fontWeight="600">
                  ${fmtUsd(p, p >= 100 ? 0 : 2)}
                </text>
                {meta.filled && (
                  <text x="56%" y={y - 4} fill={color} fontSize="9" fontFamily="ui-sans-serif" opacity="0.8">filled</text>
                )}
                {isIdle && !meta.filled && (
                  <text x="56%" y={y - 4} fill={color} fontSize="9" fontFamily="ui-sans-serif" opacity="0.8">idle</text>
                )}
              </g>
            );
          })}
          {/* Current price marker */}
          {clampedCp != null && (
            <g>
              <line x1="6%" y1={yForPrice(clampedCp)} x2="94%" y2={yForPrice(clampedCp)}
                stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeDasharray="6 3" />
              <rect x="0" y={yForPrice(clampedCp) - 8} width="44" height="16" rx="3"
                fill="rgba(255,255,255,0.95)" />
              <text x="22" y={yForPrice(clampedCp) + 4} fill="#1f2937" fontSize="10"
                textAnchor="middle" fontFamily="ui-monospace,monospace" fontWeight="700">
                ${fmtUsd(clampedCp, clampedCp >= 100 ? 0 : 2)}
              </text>
              {!cpInRange && (
                <text x="50%" y={yForPrice(clampedCp) - 10} fill="rgba(251,191,36,0.9)" fontSize="9" textAnchor="middle">
                  current price outside grid range
                </text>
              )}
            </g>
          )}
        </svg>
      </div>
      <p className="text-[10px] text-gray-500 mt-2 text-center">
        {sellSym} ↔ {buySym} · {levels.length} levels · step ${fmtUsd(range / (levels.length - 1), range / (levels.length - 1) >= 10 ? 0 : 2)}
      </p>
    </div>
  );
}

// ─── Expected returns calculator ────────────────────────────────────────────
function ExpectedReturnsCard({ preview, totalSellAmount }) {
  if (!preview) return null;
  const total = Number(totalSellAmount);
  const grossPerGrid = preview.profitPerGridPct;
  const netPerGrid = Math.max(0, grossPerGrid - ESTIMATED_FEE_ROUND_TRIP_PCT);
  const scenarios = [
    { label: '1 grid fills',           n: 1 },
    { label: `${Math.ceil(preview.numPairs / 2)} grids fill`, n: Math.ceil(preview.numPairs / 2) },
    { label: `All ${preview.numPairs} grids fill`, n: preview.numPairs },
  ];
  return (
    <div className="bg-dark-700/40 border border-dark-600 rounded-xl px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Expected returns</span>
        <span className="text-[10px] text-gray-500" title={`Round-trip fee assumption: ${ESTIMATED_FEE_ROUND_TRIP_PCT}%`}>
          net of ~{ESTIMATED_FEE_ROUND_TRIP_PCT}% fees
        </span>
      </div>
      <div className="space-y-1.5">
        {scenarios.map(s => {
          // Each "grid fill" = buy + paired sell completing. Net P&L on the
          // pair = (perPair × netPerGrid%) on the sell-token side.
          const pairAlloc = total / preview.numPairs;
          const pnlPerPair = pairAlloc * (netPerGrid / 100);
          const totalPnl = pnlPerPair * s.n;
          const totalPct = (totalPnl / total) * 100;
          return (
            <div key={s.label} className="flex items-center justify-between text-xs">
              <span className="text-gray-400">{s.label}</span>
              <span className="font-mono">
                <span className="text-emerald-400 font-semibold">+{totalPct.toFixed(2)}%</span>
                <span className="text-gray-500"> · ${fmtUsd(totalPnl, 2)}</span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[9.5px] text-gray-500 mt-2 leading-relaxed">
        Estimates assume price oscillates fully across grid steps. Real returns depend on volatility, fill order, and how often price revisits each level.
      </p>
    </div>
  );
}

// ─── Quick-start templates row ──────────────────────────────────────────────
function QuickStartTemplates({ currentPrice, onApply, disabledReason }) {
  const hasPrice = Number.isFinite(currentPrice) && currentPrice > 0;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3 h-3 text-accent-primary" />
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Quick-start templates</span>
        {!hasPrice && <span className="text-[10px] text-gray-600">(needs live price)</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {QUICK_START_TEMPLATES.map(tpl => (
          <button key={tpl.id} type="button"
            disabled={!hasPrice || !!disabledReason}
            onClick={() => onApply(tpl)}
            title={tpl.desc}
            className="text-left px-3 py-2 rounded-xl border border-dark-600 bg-dark-700/60 hover:bg-accent-primary/5 hover:border-accent-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all group">
            <div className="text-xs font-semibold text-white group-hover:text-accent-primary transition-colors">{tpl.label}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 leading-tight">{tpl.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Create Panel ───────────────────────────────────────────────────────────
function CreateGridBotPanel({ onCreated, onToast, onPairChange }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [protocol, setProtocol] = useState('cow');
  const [chainId, setChainId] = useState(connectedChainId || 1);
  const [sellSym, setSellSym] = useState('USDC');
  const [buySym,  setBuySym]  = useState('WETH');
  const [lowPrice,  setLowPrice]  = useState('');
  const [highPrice, setHighPrice] = useState('');
  const [gridCount, setGridCount] = useState(5);
  const [totalSellAmount, setTotalSellAmount] = useState('');
  const [slippagePct, setSlippagePct] = useState(1);
  const [expirySec, setExpirySec] = useState(30 * 86400);
  const [stage, setStage] = useState(null);
  const [signProgress, setSignProgress] = useState({ done: 0, total: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));

  // Live price of the BUY token (what the grid is denominated in)
  const buyCgId = SYMBOL_TO_CG[buySym] || null;
  const { data: liveData } = useTokenPrice(buyCgId, 30000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? liveData?.current_price ?? liveData?.usd ?? null;

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  useEffect(() => { onPairChange?.({ sellSym, buySym, chainId }); }, [sellSym, buySym, chainId, onPairChange]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) setChainId(PROTOCOL_CHAINS[protocol][0]);
  }, [protocol, chainId]);

  // Apply a quick-start template — needs a live price
  function applyTemplate(tpl) {
    if (!Number.isFinite(livePrice) || livePrice <= 0) return;
    const low  = livePrice * (1 - tpl.rangePct / 100);
    const high = livePrice * (1 + tpl.rangePct / 100);
    // Round prettily based on magnitude
    const round = (v) => {
      if (v >= 10000) return Math.round(v / 100) * 100;
      if (v >= 1000)  return Math.round(v / 10) * 10;
      if (v >= 100)   return Math.round(v);
      if (v >= 1)     return Number(v.toFixed(2));
      return Number(v.toFixed(4));
    };
    setLowPrice(String(round(low)));
    setHighPrice(String(round(high)));
    setGridCount(tpl.gridCount);
    setExpirySec(tpl.expirySec);
  }

  // Set range around a base price (used by ±N% quick-snap buttons)
  function setRangeAround(basePrice, pct) {
    const low  = basePrice * (1 - pct / 100);
    const high = basePrice * (1 + pct / 100);
    const round = (v) => {
      if (v >= 10000) return Math.round(v / 100) * 100;
      if (v >= 1000)  return Math.round(v / 10) * 10;
      if (v >= 100)   return Math.round(v);
      if (v >= 1)     return Number(v.toFixed(2));
      return Number(v.toFixed(4));
    };
    setLowPrice(String(round(low)));
    setHighPrice(String(round(high)));
  }

  // Live preview math (no network calls — pure derived state)
  const preview = useMemo(() => {
    const lp = Number(lowPrice), hp = Number(highPrice), n = Number(gridCount);
    const amount = Number(totalSellAmount);
    if (!(lp > 0) || !(hp > lp) || !(n >= 3) || !(amount > 0)) return null;
    const step = (hp - lp) / (n - 1);
    const profitPerGridPct = (step / lp) * 100;
    const numPairs = n - 1;
    const perPairUsd = amount / numPairs;
    const levels = [];
    for (let i = 0; i < n; i++) levels.push(Number((lp + i * step).toFixed(8)));
    return { step, profitPerGridPct, numPairs, perPairUsd, levels };
  }, [lowPrice, highPrice, gridCount, totalSellAmount]);

  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken || !buyToken || sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(lowPrice) > 0))  return 'Enter a low price > 0';
    if (!(Number(highPrice) > Number(lowPrice))) return 'High price must be greater than low';
    if (!(Number(gridCount) >= 3 && Number(gridCount) <= 20)) return 'Grid count must be 3–20';
    if (!(Number(totalSellAmount) > 0)) return 'Enter a total allocation > 0';
    if (submitting) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setStage('checking-allowance');
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setStage('approving');
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to 0 first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm');
    }
    setStage('approving');
    onToast({ type: 'info', message: `Approve ${sellSym} once. ~15–30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const totalBase = toBaseUnits(totalSellAmount, sellToken.decimals);
      const expiresAt = Math.floor(Date.now() / 1000) + Number(expirySec);

      // 1. Prepare — backend computes levels + builds 2*(N-1) typed-data blobs
      setStage('preparing');
      const prep = await prepareDexGridBot({
        protocol, chainId,
        owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        lowPrice:  Number(lowPrice),
        highPrice: Number(highPrice),
        gridCount: Number(gridCount),
        totalSellAmount: totalBase,
        slippagePct: Number(slippagePct),
        expiresAt,
      });
      const { gridBotId, levels, pairs, spender } = prep.data;
      const totalSigs = pairs.length * 2;
      setSignProgress({ done: 0, total: totalSigs });

      // 2. Approve sell-token allowance once for the full amount
      await ensureAllowance(spender, BigInt(totalBase));

      // 3. Sign each pair's buy + sell sequentially
      const signedPairs = [];
      let done = 0;
      for (const pair of pairs) {
        setStage('signing-buy');
        const buySig = await signTypedDataAsync({
          domain: pair.buy.typedData.domain,
          types:  pair.buy.typedData.types,
          primaryType: pair.buy.typedData.primaryType,
          message: pair.buy.order,
        });
        done++; setSignProgress({ done, total: totalSigs });

        setStage('signing-sell');
        const sellSig = await signTypedDataAsync({
          domain: pair.sell.typedData.domain,
          types:  pair.sell.typedData.types,
          primaryType: pair.sell.typedData.primaryType,
          message: pair.sell.order,
        });
        done++; setSignProgress({ done, total: totalSigs });

        signedPairs.push({
          pairIdx:      pair.pairIdx,
          buyLevelIdx:  pair.buyLevelIdx,
          sellLevelIdx: pair.sellLevelIdx,
          buyPrice:     pair.buyPrice,
          sellPrice:    pair.sellPrice,
          buy: {
            order:      pair.buy.order,
            signature:  buySig,
            orderId:    pair.buy.orderId,
            sellAmount: pair.buy.sellAmount,
            buyAmount:  pair.buy.buyAmount,
          },
          sell: {
            order:      pair.sell.order,
            signature:  sellSig,
            orderId:    pair.sell.orderId,
            sellAmount: pair.sell.sellAmount,
            buyAmount:  pair.sell.buyAmount,
          },
        });
      }

      // 4. Commit
      setStage('committing');
      const result = await commitDexGridBot({
        gridBotId, protocol, chainId, owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        lowPrice:  Number(lowPrice),
        highPrice: Number(highPrice),
        gridCount: Number(gridCount),
        totalSellAmount: totalBase,
        slippagePct: Number(slippagePct),
        expiresAt,
        levels,
        pairs: signedPairs,
      });

      const errs = result.data?.submitErrors || [];
      if (errs.length === pairs.length) {
        throw new Error('All buy orders failed to submit. Check protocol availability and try again.');
      }
      const partial = errs.length > 0 ? ` (${errs.length} of ${pairs.length} buys failed — others active)` : '';
      onToast({ type: 'success', message: `Grid Bot armed — ${pairs.length} pairs across ${gridCount} levels${partial}.` });
      setLowPrice(''); setHighPrice(''); setTotalSellAmount('');
      onCreated(result.data?.record);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create grid bot';
      onToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
      setStage(null);
      setSignProgress({ done: 0, total: 0 });
    }
  }

  const stageLabel = (() => {
    if (stage === 'preparing') return 'Computing grid levels…';
    if (stage === 'checking-allowance') return 'Checking allowance…';
    if (stage === 'approving') return `Approving ${sellSym}…`;
    if (stage === 'signing-buy' || stage === 'signing-sell') {
      return `Sign ${signProgress.done + 1} of ${signProgress.total} (${stage === 'signing-buy' ? 'buy' : 'sell'} leg)…`;
    }
    if (stage === 'committing') return 'Saving grid…';
    return 'Arm Grid Bot';
  })();

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
          <Grid3X3 className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New Grid Bot</h3>
          <p className="text-gray-500 text-xs">Buy-low / sell-high across a price range. Pre-signed on CoW/Velora — runs autonomously.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">Wallet is on chain {connectedChainId}; switch to {CHAINS.find(c => c.id === chainId)?.label}.</p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-40">
            {switchingChain ? 'Switching…' : `Switch to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} type="button" onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white shadow-[0_0_0_1px_rgba(245,158,11,0.1)]' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW' : 'Velora'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Pair */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Spend</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Total amount</label>
            <input type="number" min="0" step="any" value={totalSellAmount}
              onChange={e => setTotalSellAmount(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Trade</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
        </div>

        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Live price + ± snap buttons */}
        <LivePriceBadge symbol={buySym} onSetRange={setRangeAround} />

        {/* Quick-start templates */}
        <QuickStartTemplates currentPrice={livePrice} onApply={applyTemplate} disabledReason={submitting ? 'submitting' : null} />

        {/* Grid range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingDown className="w-3 h-3 text-red-400" /> Low price <span className="text-gray-600 normal-case">(USD)</span>
              <InfoTip side="top">Lowest buy level. If price drops below this, the grid stalls — no buy levels left to fire. Use the ±5/10/20% buttons on the live-price badge to set this around current.</InfoTip>
            </label>
            <input type="number" min="0" step="any" value={lowPrice}
              onChange={e => setLowPrice(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-emerald-400" /> High price <span className="text-gray-600 normal-case">(USD)</span>
              <InfoTip side="top">Highest sell level. If price rises above this, the grid stalls — no sell levels left to fire. Cancel and rebuild around the new range if break is persistent.</InfoTip>
            </label>
            <input type="number" min="0" step="any" value={highPrice}
              onChange={e => setHighPrice(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
        </div>

        {/* Grid count */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Grid count <span className="text-gray-600 normal-case">({gridCount} levels → {Math.max(0, gridCount - 1)} pairs → {Math.max(0, (gridCount - 1) * 2)} signatures)</span>
            <InfoTip side="right">More levels = denser grid, more fills, smaller profit per fill. Fewer = sparser, bigger profit per fill, more wait. 5-7 is the sweet spot. Each level pair = 2 EIP-712 popups.</InfoTip>
          </label>
          <input type="range" min="3" max="20" step="1" value={gridCount}
            onChange={e => setGridCount(Number(e.target.value))}
            className="w-full accent-accent-primary" />
          <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
            <span>3</span><span>10</span><span>20</span>
          </div>
        </div>

        {/* Visual grid ladder preview */}
        <GridLadder levels={preview?.levels || []} currentPrice={livePrice}
          sellSym={sellSym} buySym={buySym} height={200} />

        {/* Preview stats card */}
        {preview && (
          <div className="bg-dark-700/40 border border-dark-600 rounded-xl px-3 py-2 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-400">Step size</span><span className="text-white">${fmtUsd(preview.step, preview.step >= 10 ? 0 : 2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Profit per grid (gross)</span><span className="text-emerald-400">+{preview.profitPerGridPct.toFixed(2)}%</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Allocation per pair</span><span className="text-white">~${fmtUsd(preview.perPairUsd, 2)} {sellSym}</span></div>
          </div>
        )}

        {/* Expected-returns calculator */}
        <ExpectedReturnsCard preview={preview} totalSellAmount={totalSellAmount} />

        {/* Slippage + expiry */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Slippage</label>
            <input type="number" min="0.1" max="10" step="0.1" value={slippagePct}
              onChange={e => setSlippagePct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Watch for</label>
            <select value={expirySec} onChange={e => setExpirySec(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {EXPIRY_OPTIONS.map(o => <option key={o.sec} value={o.sec}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className={`relative w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden
            ${canSubmit
              ? 'bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 shadow-lg shadow-accent-primary/20 hover:shadow-xl hover:shadow-accent-primary/30 hover:-translate-y-px'
              : 'bg-dark-700 text-gray-500'}`}>
          {submitting ? <Spinner size="sm" /> : <Grid3X3 className="w-4 h-4" />}
          {stageLabel}
          {canSubmit && !submitting && (
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700 pointer-events-none" />
          )}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center">
            One token approval + {Math.max(0, (gridCount - 1) * 2)} signatures total. After that, the grid runs autonomously.
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Grid Bot Row (expandable) ──────────────────────────────────────────────
function GridBotRow({ record, onCancel, onPause, onResume, onRefill, busy }) {
  const [expanded, setExpanded] = useState(false);
  const protoLabel = record.protocol === 'cow' ? 'CoW' : 'Velora';
  const cfg = record.config || {};
  const pair = record.pair || {};
  const buyCgId = SYMBOL_TO_CG[pair.buySymbol] || null;
  const { data: liveData } = useTokenPrice(buyCgId, 60000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? liveData?.current_price ?? liveData?.usd ?? null;

  // Build levelMeta from the record so the ladder colours each level by what
  // the bot has actually done with it.
  const levelPrices = Array.isArray(record.levels) ? record.levels.map(l => l.price) : [];
  const levelMeta = Array.isArray(record.levels) ? record.levels.map((l) => {
    const filled = Array.isArray(l.fills) && l.fills.length > 0;
    // A level is "idle" when there's no active order AND no past fills. We
    // deliberately don't infer buy/sell from current price here, because the
    // ladder is showing actual bot state — not a hypothetical preview.
    if (l.currentSide) return { side: l.currentSide, filled: false };
    if (filled) return { side: 'idle', filled: true };
    return { side: 'idle', filled: false };
  }) : [];

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      {/* Header row */}
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left flex items-start justify-between gap-3 hover:bg-dark-600/20 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Grid3X3 className="w-3.5 h-3.5 text-accent-primary" />
            <span className="text-white text-sm font-semibold">
              {formatBaseUnits(cfg.totalSellAmount, pair.sellDecimals || 6)} {pair.sellSymbol} → {pair.buySymbol}
            </span>
            <StatusBadge status={record.status} />
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-1.5">
            <p className="text-gray-400 text-xs">
              <span className="text-gray-500">Range:</span> <span className="text-white">${fmtUsd(cfg.lowPrice, cfg.lowPrice >= 100 ? 0 : 2)} – ${fmtUsd(cfg.highPrice, cfg.highPrice >= 100 ? 0 : 2)}</span>
              <span className="text-gray-500"> · {cfg.gridCount} levels</span>
            </p>
            <p className="text-xs">
              <span className="text-gray-500">Fills:</span> <span className={record.fillsCount > 0 ? 'text-emerald-400 font-semibold' : 'text-gray-300'}>{record.fillsCount || 0}</span>
            </p>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 text-xs">Pool:</span>
              <PoolCapacityBar pool={record.refillPool} />
            </div>
          </div>
          {record.lastFillAt && (
            <p className="text-gray-500 text-[11px] mt-1">Last fill: {fmtTime(record.lastFillAt)}</p>
          )}
          {record.completedReason && (
            <p className="text-blue-300 text-[11px] mt-0.5">Reason: {record.completedReason}</p>
          )}
          {Array.isArray(record.submitErrors) && record.submitErrors.length > 0 && (
            <p className="text-red-400 text-[11px] mt-0.5">⚠ {record.submitErrors.length} buy leg{record.submitErrors.length === 1 ? '' : 's'} failed to submit</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {(record.status === 'armed') && (
            <button onClick={() => onRefill(record.gridBotId)} disabled={busy} title="Sign more cycles"
              className="p-1.5 rounded-lg bg-dark-700 text-accent-primary hover:bg-accent-primary/10 disabled:opacity-50 transition-colors">
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          {(record.status === 'armed') && (
            <button onClick={() => onPause(record.gridBotId)} disabled={busy} title="Pause"
              className="p-1.5 rounded-lg bg-dark-700 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 transition-colors">
              <PauseCircle className="w-3.5 h-3.5" />
            </button>
          )}
          {(record.status === 'paused') && (
            <button onClick={() => onResume(record.gridBotId)} disabled={busy} title="Resume"
              className="p-1.5 rounded-lg bg-dark-700 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
              <PlayCircle className="w-3.5 h-3.5" />
            </button>
          )}
          {(record.status === 'armed' || record.status === 'paused') && (
            <button onClick={() => onCancel(record.gridBotId)} disabled={busy} title="Cancel grid"
              className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[fadeIn_0.2s_ease-out]">
          {levelPrices.length > 0 && (
            <GridLadder levels={levelPrices} currentPrice={livePrice}
              levelMeta={levelMeta} sellSym={pair.sellSymbol} buySym={pair.buySymbol} height={200} />
          )}

          {/* Per-level state table */}
          <div className="bg-dark-700/30 rounded-xl border border-dark-600 overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-dark-800/60">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Price</div>
              <div className="col-span-3">State</div>
              <div className="col-span-2 text-center">Fills</div>
              <div className="col-span-3 text-right">Order</div>
            </div>
            {record.levels?.map((l, idx) => {
              const filledCount = Array.isArray(l.fills) ? l.fills.length : 0;
              const stateLabel = l.currentSide
                ? (l.currentSide === 'buy' ? 'buy live' : 'sell live')
                : (filledCount > 0 ? 'cycle done' : 'idle');
              const stateClass = l.currentSide === 'buy' ? 'text-emerald-400'
                : l.currentSide === 'sell' ? 'text-red-400'
                : filledCount > 0 ? 'text-blue-300'
                : 'text-gray-500';
              return (
                <div key={idx} className={`grid grid-cols-12 px-3 py-1.5 text-xs ${idx < record.levels.length - 1 ? 'border-b border-dark-600/30' : ''} hover:bg-dark-600/20`}>
                  <div className="col-span-1 text-gray-500 font-mono">L{idx + 1}</div>
                  <div className="col-span-3 text-white font-mono">${fmtUsd(l.price, l.price >= 100 ? 0 : 2)}</div>
                  <div className={`col-span-3 ${stateClass}`}>{stateLabel}</div>
                  <div className="col-span-2 text-center text-gray-300">{filledCount}</div>
                  <div className="col-span-3 text-right text-gray-500 font-mono truncate" title={l.currentOrderId || '—'}>
                    {l.currentOrderId ? `${l.currentOrderId.slice(0, 6)}…` : '—'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent fills feed */}
          {record.levels?.some(l => Array.isArray(l.fills) && l.fills.length > 0) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Recent fills</div>
              <div className="space-y-1">
                {record.levels
                  .flatMap(l => (l.fills || []).map(f => ({ ...f, levelIdx: l.levelIdx, levelPrice: l.price })))
                  .sort((a, b) => Date.parse(b.filledAt || 0) - Date.parse(a.filledAt || 0))
                  .slice(0, 5)
                  .map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-3 py-1.5 bg-dark-700/40 rounded-lg">
                      <span className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${f.side === 'buy' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <span className={f.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{f.side === 'buy' ? 'Bought' : 'Sold'}</span>
                        <span className="text-gray-500">at L{f.levelIdx + 1} (${fmtUsd(f.levelPrice, f.levelPrice >= 100 ? 0 : 2)})</span>
                      </span>
                      <span className="text-gray-500 text-[10px]">{fmtTime(f.filledAt)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function DexGridBotPage() {
  const { signTypedDataAsync } = useSignTypedData();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [chartPair, setChartPair] = useState('ETH/USDT');

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listDexGridBots(); setBots(r.data?.bots || []); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Could not load grid bots' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (record) => {
    if (record) setBots(prev => [record, ...prev.filter(b => b.gridBotId !== record.gridBotId)]);
  };
  const handlePause = async (id) => {
    setBusy(id);
    try {
      await pauseDexGridBot(id);
      await reload();
      setToast({ type: 'success', message: 'Grid bot paused. Refills suspended.' });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Pause failed' }); }
    finally { setBusy(null); }
  };
  const handleResume = async (id) => {
    setBusy(id);
    try {
      await resumeDexGridBot(id);
      await reload();
      setToast({ type: 'success', message: 'Grid bot resumed.' });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Resume failed' }); }
    finally { setBusy(null); }
  };
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this grid? All open orders will be soft-cancelled; unfilled CoW orders left on the orderbook expire on their own validTo.')) return;
    setBusy(id);
    try {
      await cancelDexGridBot(id);
      await reload();
      setToast({ type: 'success', message: 'Grid bot cancelled.' });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Cancel failed' }); }
    finally { setBusy(null); }
  };
  const handleRefill = async (id) => {
    setBusy(id);
    try {
      const prep = await refillPrepareDexGridBot(id, { extraCyclesPerLevel: 1 });
      const { pairs } = prep.data;
      if (!pairs?.length) throw new Error('Nothing to sign');
      const signed = [];
      for (const pair of pairs) {
        const buySig = await signTypedDataAsync({
          domain: pair.buy.typedData.domain, types: pair.buy.typedData.types,
          primaryType: pair.buy.typedData.primaryType, message: pair.buy.order,
        });
        const sellSig = await signTypedDataAsync({
          domain: pair.sell.typedData.domain, types: pair.sell.typedData.types,
          primaryType: pair.sell.typedData.primaryType, message: pair.sell.order,
        });
        signed.push({
          cycleIdx: pair.cycleIdx, pairIdx: pair.pairIdx,
          buyLevelIdx: pair.buyLevelIdx, sellLevelIdx: pair.sellLevelIdx,
          buyPrice: pair.buyPrice, sellPrice: pair.sellPrice,
          buy:  { order: pair.buy.order,  signature: buySig,  orderId: pair.buy.orderId,
                  sellAmount: pair.buy.sellAmount, buyAmount: pair.buy.buyAmount },
          sell: { order: pair.sell.order, signature: sellSig, orderId: pair.sell.orderId,
                  sellAmount: pair.sell.sellAmount, buyAmount: pair.sell.buyAmount },
        });
      }
      const r = await refillCommitDexGridBot(id, { pairs: signed });
      await reload();
      setToast({ type: 'success', message: `Added ${r.data?.added || 0} orders to grid pool.` });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Refill failed' }); }
    finally { setBusy(null); }
  };

  const active  = bots.filter(b => b.status === 'armed' || b.status === 'paused');
  const history = bots.filter(b => !['armed', 'paused'].includes(b.status));

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes grid-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/25 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <Grid3X3 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Grid Bot</h1>
            <p className="text-gray-400 text-sm">Buy-low / sell-high across a price range, autonomously. Pre-signed on CoW/Velora — every fill triggers the next paired leg.</p>
          </div>
        </div>
      </div>

      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateGridBotPanel onCreated={handleCreated} onToast={setToast}
          onPairChange={({ sellSym, buySym }) => setChartPair(chartPairFor(sellSym, buySym))} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active grids</h3>
              <p className="text-gray-500 text-xs">{active.length} running</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Target className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active grid bots. Set a price range and let the grid trade it for you.</p>
              <p className="text-gray-600 text-[11px] mt-1">Tip: use a quick-start template to skip the math.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(b => (
                <GridBotRow key={b.gridBotId} record={b}
                  onCancel={handleCancel} onPause={handlePause}
                  onResume={handleResume} onRefill={handleRefill}
                  busy={busy === b.gridBotId} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(b => (
              <GridBotRow key={b.gridBotId} record={b}
                onCancel={handleCancel} onPause={handlePause}
                onResume={handleResume} onRefill={handleRefill}
                busy={busy === b.gridBotId} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
